import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpdateSentiment } from "@/hooks/useExternalReviews";
import { ApiError } from "@/services/api";
import type { ExternalReviewAdmin } from "@/types/externalReview";
import { PLATFORM_LABELS } from "@/types/externalReview";

const PRESETS: { label: string; value: number }[] = [
  { label: "Negative (−1)", value: -1 },
  { label: "Neutral (0)", value: 0 },
  { label: "Positive (+1)", value: 1 },
];

interface SentimentDialogProps {
  open: boolean;
  review: ExternalReviewAdmin | null;
  onOpenChange: (open: boolean) => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

export function SentimentDialog({
  open,
  review,
  onOpenChange,
}: SentimentDialogProps) {
  const updateSentiment = useUpdateSentiment();
  const [localReview, setLocalReview] = useState<ExternalReviewAdmin | null>(
    null,
  );
  const [score, setScore] = useState("0");
  const [model, setModel] = useState("");

  useEffect(() => {
    if (open && review) {
      setLocalReview(review);
      setScore("0");
      setModel("");
    }
    if (!open) {
      setLocalReview(null);
    }
  }, [open, review]);

  const displayReview = localReview ?? review;

  const parsedScore = Number(score);
  const scoreValid =
    score.trim() !== "" &&
    Number.isFinite(parsedScore) &&
    parsedScore >= -1 &&
    parsedScore <= 1;

  const handleSubmit = async () => {
    if (!displayReview || !scoreValid) return;
    try {
      await updateSentiment.mutateAsync({
        reviewId: displayReview.id,
        sentiment_score: parsedScore,
        sentiment_method: "manual",
        sentiment_model: model.trim() || null,
      });
      toast.success(`Sentiment set for review #${displayReview.id}`);
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.message);
        return;
      }
      toast.error(
        error instanceof Error ? error.message : "Failed to update sentiment",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Set sentiment</DialogTitle>
          <DialogDescription>
            Score this external review on a scale from −1 (negative) to +1
            (positive). Method is locked to manual.
          </DialogDescription>
        </DialogHeader>

        {displayReview ? (
          <div className="space-y-4">
            <div className="rounded-md border p-3 space-y-2 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">
                  {PLATFORM_LABELS[displayReview.platform] ??
                    displayReview.platform}
                </Badge>
                <span className="font-mono text-xs text-muted-foreground">
                  {displayReview.source_id}
                </span>
                {displayReview.author_handle ? (
                  <span className="text-muted-foreground">
                    @{displayReview.author_handle}
                  </span>
                ) : null}
              </div>
              <p className="whitespace-pre-wrap">
                {displayReview.text?.trim() || (
                  <span className="text-muted-foreground italic">No text</span>
                )}
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>Scraped: {formatDate(displayReview.scraped_at)}</span>
                <span>Posted: {formatDate(displayReview.posted_at)}</span>
                {displayReview.native_rating != null ? (
                  <span>
                    Rating: {displayReview.native_rating}
                    {displayReview.native_rating_scale != null
                      ? ` / ${displayReview.native_rating_scale}`
                      : ""}
                  </span>
                ) : null}
              </div>
              <a
                href={displayReview.url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-primary hover:underline break-all"
              >
                {displayReview.url}
              </a>
            </div>

            <div className="space-y-2">
              <Label>Sentiment score</Label>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((preset) => (
                  <Button
                    key={preset.value}
                    type="button"
                    size="sm"
                    variant={
                      scoreValid && parsedScore === preset.value
                        ? "default"
                        : "outline"
                    }
                    onClick={() => setScore(String(preset.value))}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
              <Input
                type="number"
                min={-1}
                max={1}
                step={0.1}
                value={score}
                onChange={(e) => setScore(e.target.value)}
                aria-invalid={!scoreValid}
              />
              {!scoreValid ? (
                <p className="text-xs text-destructive">
                  Score must be a number between −1 and 1.
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="sentiment-model">
                Sentiment model{" "}
                <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="sentiment-model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="e.g. human-labeler"
              />
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={
              !displayReview || !scoreValid || updateSentiment.isPending
            }
            onClick={() => void handleSubmit()}
          >
            {updateSentiment.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Save sentiment"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
